/* eslint-disable formatjs/no-literal-string-in-jsx */
import { lazy, PropsWithChildren, Suspense } from "react";

import { Button } from "@mui/material";
import Grid from "@mui/material/Grid";
import { Theme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import { AxiosError, isAxiosError } from "axios";
import { FormattedMessage } from "react-intl";

import { PrimaryButton } from "@/common/components/buttons/primary-button/primary-button";
import { useToggle } from "@/shared/hooks/useToggle";

import errorPage40x from "../../../../assets/img/error-page-404.svg?url";
import errorPage500 from "../../../../assets/img/error-page-500.svg?url";
import errorPage503 from "../../../../assets/img/error-page-503.svg?url";
import { RsaaApiError } from "../../../../types";

const TractorGame = lazy(() =>
  import("./TractorGame").then((m) => ({ default: m.TractorGame })),
);

const images = {
  0: "",
  404: errorPage40x,
  500: errorPage500,
  503: errorPage503,
};

type Props = {
  handle400s?: boolean;
  error40xHeadingTranslId?: string;
  error40xMessageTranslId?: string;
  error?: Partial<RsaaApiError> | Error;
};

type ErrorHandlerFn<E extends AxiosError | Partial<RsaaApiError>> = (
  error: E,
) => {
  is500: boolean;
  is503: boolean;
  is40x: boolean;
  code: number;
};

const createErrorResult = (
  is500: boolean,
  is503: boolean,
  is40x: boolean,
  code: number,
) => ({
  is500,
  is503,
  is40x,
  code,
});

const isError40x = (code: string) => /40[0-4]/.test(code);

const handleAxiosError: ErrorHandlerFn<AxiosError> = (error) => {
  const is500 = error.response?.status === 500;
  const is503 = error.response?.status === 503;
  const is40x = error.response?.status
    ? isError40x(error.response?.status.toString())
    : false;
  const code = error.response?.status ?? 0;
  return createErrorResult(is500, is503, is40x, code);
};

const handleRsaaApiError: ErrorHandlerFn<Partial<RsaaApiError>> = (error) => {
  const is500 = error.code === 500;
  const is503 = error.code === 503;
  const is40x = error.code ? isError40x(error.code.toString()) : false;
  const code = error.code ?? 0;
  return createErrorResult(is500, is503, is40x, code);
};

const CfErrorPage = ({
  children,
  error = {},
  error40xHeadingTranslId = "",
  error40xMessageTranslId = "",
  handle400s = false,
}: PropsWithChildren<Props>) => {
  const classes = useStyle();
  const game = useToggle();

  // An Error instance has no enumerable own keys, so counting them misses every thrown error -
  // the ones an ErrorBoundary hands over, and the ones a query function throws while mapping a 200.
  // ErrorService.getResErrorDto() is the opposite trap: for a request that did not fail it still
  // yields `{ isError: false, code: null, response: null }` - three keys and no status - which
  // counted as an error would land in the unhandled-failure branch below and paint the server
  // error over a perfectly good 200. The DTO says so itself, trust it.
  const isNoErrorDto =
    !(error instanceof Error) &&
    (error as Partial<RsaaApiError>).isError === false;
  const hasError =
    !isNoErrorDto && (error instanceof Error || !!Object.keys(error).length);

  let result = createErrorResult(false, false, false, 0);
  if (hasError) {
    if (isAxiosError(error)) {
      result = handleAxiosError(error);
    } else {
      result = handleRsaaApiError(error as Partial<RsaaApiError>);
    }
  }

  // An error carrying no HTTP status is still a failure: a thrown Error, or a request that never
  // got a response. Falling through to the children leaves the screen on its loading state
  // forever, and as an ErrorBoundary fallback there are no children at all - a blank page. Show
  // the server error instead. A status we deliberately do not handle (a 409, or a 40x with
  // handle400s off) keeps passing through as before.
  const isUnhandledFailure = hasError && result.code === 0;
  const code = isUnhandledFailure ? 500 : result.code;

  const shouldCatch =
    result.is500 ||
    result.is503 ||
    (result.is40x && handle400s) ||
    isUnhandledFailure;

  let errorContent;
  if (game.on) {
    errorContent = (
      <Suspense fallback={null}>
        <TractorGame errorCode={code} />
      </Suspense>
    );
  } else {
    errorContent = (
      <Grid
        alignItems="center"
        container
        direction="column"
        justifyContent="center"
        spacing={2}
      >
        <Grid item>
          <img
            alt={`Error ${code}`}
            className={classes.image}
            src={images[code as keyof typeof images]}
          />
        </Grid>
        <Grid item>
          <h1 className={classes.heading} data-testid="error-title">
            <FormattedMessage
              id={
                result.is40x ? error40xHeadingTranslId : `error.heading.${code}`
              }
            />
          </h1>
        </Grid>
        {!result.is40x && (
          <Grid item style={{ textAlign: "center" }} xs={8}>
            <FormattedMessage id={`error.message.${code}`} />{" "}
            <a href="mailto:help@cleverfarm.cz">help@cleverfarm.cz</a>.
          </Grid>
        )}
        {!result.is40x && (
          <Grid className={classes.actions} item>
            <PrimaryButton
              data-testid="error-recheck"
              onClick={() => window.location.reload()}
              variant="full"
            >
              <FormattedMessage id="error.recheck" />
            </PrimaryButton>
            <PrimaryButton
              data-testid="error-game-play"
              onClick={game.setOn}
              variant="outlined"
            >
              <FormattedMessage id="error.game.play" />
            </PrimaryButton>
          </Grid>
        )}
        {result.is40x && (
          <Grid
            alignItems="center"
            container
            justifyContent="center"
            spacing={0}
          >
            <Grid item style={{ textAlign: "center" }} xs={12}>
              <FormattedMessage id={error40xMessageTranslId} />
            </Grid>
            <Button
              className={classes.button}
              color="primary"
              onClick={() => history.back()}
              size="medium"
              variant="contained"
            >
              <FormattedMessage id="common.back" />
            </Button>
          </Grid>
        )}
      </Grid>
    );
  }

  return shouldCatch ? (
    <div className={classes.wrapper}>{errorContent}</div>
  ) : (
    <>{children}</>
  );
};

const useStyle = makeStyles((theme: Theme) => ({
  wrapper: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(2),
  },
  image: {
    width: 250,
    height: "auto",
  },
  heading: {
    marginBottom: 10,
  },
  button: {
    marginTop: 40,
  },
  actions: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

export { CfErrorPage };
